import { useEffect, useRef, useState } from 'react';
import { REFERENCE_PLACES_PIN_STYLE } from '../styles/mapTheme';
import { waitForMapStyleReady } from '../utils/waitForMapStyleReady';
import { canAccessMapStyle } from '../utils/canAccessMapStyle';
import { bindReferencePlaceImages, ensureReferencePlaceImages } from '../utils/referencePlaceIcons';
import {
  getSignedPmtilesUrl,
  refreshPmtilesVectorSourceIfNeeded,
} from '../../Utils/pmtilesAuth';
import {
  buildReferencePlaceIconImageExpression,
  buildReferencePlacesKindFilter,
  isReferencePlacesGeoJsonSource,
  moveReferencePlacesForeground,
  referencePlacesInsertBeforeLayerId,
  REFERENCE_PLACES_LAYER_CIRCLES_ID,
  REFERENCE_PLACES_LAYER_DOTS_ID,
  REFERENCE_PLACES_MAX_ZOOM,
  REFERENCE_PLACES_MIN_ZOOM,
  REFERENCE_PLACES_PMTILES_URL,
  REFERENCE_PLACES_SOURCE_ID,
  REFERENCE_PLACES_SOURCE_LAYER,
} from '../utils/referencePlaces';

function removeReferencePlacesTiles(map) {
  if (!map) return;
  try {
    if (isReferencePlacesGeoJsonSource(map)) return;
    if (map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID)) {
      map.removeLayer(REFERENCE_PLACES_LAYER_DOTS_ID);
    }
    if (map.getLayer(REFERENCE_PLACES_LAYER_CIRCLES_ID)) {
      map.removeLayer(REFERENCE_PLACES_LAYER_CIRCLES_ID);
    }
    if (map.getSource(REFERENCE_PLACES_SOURCE_ID)) {
      map.removeSource(REFERENCE_PLACES_SOURCE_ID);
    }
  } catch (_) {
    // mapa o estilo aún no listos
  }
}

function ensureTilesSourceAndLayer(map, signedUrl, kinds) {
  if (!map.getSource(REFERENCE_PLACES_SOURCE_ID)) {
    map.addSource(REFERENCE_PLACES_SOURCE_ID, {
      type: 'vector',
      url: signedUrl,
    });
  }

  if (!map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID)) {
    map.addLayer({
      id: REFERENCE_PLACES_LAYER_DOTS_ID,
      type: 'symbol',
      source: REFERENCE_PLACES_SOURCE_ID,
      'source-layer': REFERENCE_PLACES_SOURCE_LAYER,
      minzoom: REFERENCE_PLACES_MIN_ZOOM,
      maxzoom: REFERENCE_PLACES_MAX_ZOOM,
      filter: buildReferencePlacesKindFilter(kinds),
      layout: {
        'icon-image': buildReferencePlaceIconImageExpression(),
        'icon-size': REFERENCE_PLACES_PIN_STYLE.iconSize,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
        'icon-padding': 0,
      },
    }, referencePlacesInsertBeforeLayerId(map));
  } else {
    map.setFilter(REFERENCE_PLACES_LAYER_DOTS_ID, buildReferencePlacesKindFilter(kinds));
  }

  ensureReferencePlaceImages(map);
  moveReferencePlacesForeground(map);
}

/**
 * Pines desde `tiles/reference_places.pmtiles` (firma JWT). Filtro cliente
 * por `kind`. No llama `GET /getreferenceplaces`.
 *
 * No gatear con `isStyleLoaded()`: en SharedList (y share de lote) queda
 * false mientras cargan PMTiles de parcelas; addSource/addLayer igual pueden
 * ir (igual que el fallback GeoJSON). Reintentar en styledata + idle.
 *
 * @returns {{ ready: boolean, failed: boolean }}
 */
export function useReferencePlacesTilesLayer({
  mapInstanceRef,
  enabled,
  kinds = [],
}) {
  const kindsRef = useRef(kinds);
  kindsRef.current = kinds;
  const [status, setStatus] = useState('idle');
  const [mapAttachVersion, setMapAttachVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    if (mapInstanceRef.current) {
      setMapAttachVersion((version) => version + 1);
      return undefined;
    }
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (mapInstanceRef.current) {
        setMapAttachVersion((version) => version + 1);
        window.clearInterval(intervalId);
        return;
      }
      if (attempts >= 120) window.clearInterval(intervalId);
    }, 50);
    return () => window.clearInterval(intervalId);
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled) {
      removeReferencePlacesTiles(map);
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;
    let paintQueued = false;
    const moveEndHandler = () => {
      if (!enabled) return;
      void refreshPmtilesVectorSourceIfNeeded(
        map,
        REFERENCE_PLACES_SOURCE_ID,
        REFERENCE_PLACES_PMTILES_URL,
      );
    };

    const paint = async () => {
      if (cancelled) return;
      paintQueued = false;
      // Igual que usePmtilesLayers / GeoJSON POI: basta con que exista style.
      // isStyleLoaded() suele ser false en lista compartida con parcelas a medias.
      if (!canAccessMapStyle(map)) return;

      try {
        bindReferencePlaceImages(map);

        if (map.getSource(REFERENCE_PLACES_SOURCE_ID) && map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID)) {
          map.setFilter(
            REFERENCE_PLACES_LAYER_DOTS_ID,
            buildReferencePlacesKindFilter(kindsRef.current),
          );
          ensureReferencePlaceImages(map);
          moveReferencePlacesForeground(map);
          setStatus('ready');
          return;
        }
      } catch (_) {
        // estilo a medias: idle/styledata reintenta
        return;
      }

      const signedUrl = await getSignedPmtilesUrl(REFERENCE_PLACES_PMTILES_URL);
      if (cancelled) return;
      if (!signedUrl) {
        removeReferencePlacesTiles(map);
        setStatus('failed');
        return;
      }

      // Tras el await el mapa pudo desmontarse o recrear style.
      if (!canAccessMapStyle(map)) return;

      try {
        ensureTilesSourceAndLayer(map, signedUrl, kindsRef.current);
        setStatus('ready');
      } catch (error) {
        console.error('No se pudieron pintar los puntos de interés (tiles)', error);
        // Source sin layer bloquea el fallback GeoJSON (mismo id). Limpiar.
        // No marcar failed si el style está mutando: idle reintenta.
        removeReferencePlacesTiles(map);
        if (!canAccessMapStyle(map)) return;
        setStatus('failed');
      }
    };

    const schedulePaint = () => {
      if (cancelled || paintQueued) return;
      paintQueued = true;
      window.requestAnimationFrame(() => { void paint(); });
    };

    waitForMapStyleReady(map).then(() => { void paint(); });

    const hasTilesLayer = () => {
      try {
        return Boolean(
          map.getSource(REFERENCE_PLACES_SOURCE_ID)
          && map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID),
        );
      } catch (_) {
        return false;
      }
    };

    // styledata: parcelas nuevas se agregan al tope; remarcar pines + iconos.
    const handleStyleData = () => {
      if (cancelled || !canAccessMapStyle(map)) return;
      if (hasTilesLayer()) {
        ensureReferencePlaceImages(map);
        moveReferencePlacesForeground(map);
        return;
      }
      schedulePaint();
    };

    // idle: en SharedList isStyleLoaded() suele ser false mientras cargan
    // teselas; styledata no alcanza y el primer toggle quedaba sin source.
    const handleIdle = () => {
      if (cancelled || !canAccessMapStyle(map)) return;
      if (hasTilesLayer()) return;
      schedulePaint();
    };

    map.on('styledata', handleStyleData);
    map.on('idle', handleIdle);
    map.on('moveend', moveEndHandler);

    return () => {
      cancelled = true;
      try { map.off('styledata', handleStyleData); } catch (_) { /* noop */ }
      try { map.off('idle', handleIdle); } catch (_) { /* noop */ }
      try { map.off('moveend', moveEndHandler); } catch (_) { /* noop */ }
    };
  }, [enabled, mapInstanceRef, mapAttachVersion]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map || !canAccessMapStyle(map)) return;
    if (!map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID)) return;
    try {
      map.setFilter(REFERENCE_PLACES_LAYER_DOTS_ID, buildReferencePlacesKindFilter(kinds));
      ensureReferencePlaceImages(map);
      moveReferencePlacesForeground(map);
    } catch (_) {
      // estilo a medias
    }
  }, [enabled, kinds, mapInstanceRef, mapAttachVersion]);

  useEffect(() => {
    if (enabled) return;
    removeReferencePlacesTiles(mapInstanceRef.current);
  }, [enabled, mapInstanceRef]);

  useEffect(() => () => {
    removeReferencePlacesTiles(mapInstanceRef.current);
  }, [mapInstanceRef]);

  return {
    ready: status === 'ready',
    failed: status === 'failed',
  };
}
